/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState } from "react";

// react-router-dom components
import { Link, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// @mui material components
import Card from "@mui/material/Card";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";
import { login } from "store/slices/firebaseSlice";
import { resendVerificationEmail } from "services/firebase";

// Images
import bgImage from "assets/images/bg-sign-in-basic.png";
import { firebase } from "store/slices/firebaseSlice";

function Basic() {
  const [rememberMe, setRememberMe] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorSB, setErrorSB] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showResendButton, setShowResendButton] = useState(false);
  const [successSB, setSuccessSB] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isResending, setIsResending] = useState(false);
  const { isAuth, error } = useSelector(firebase);

  const dispatch = useDispatch();

  const handleSetRememberMe = () => setRememberMe(!rememberMe);

  const openErrorSB = () => setErrorSB(true);
  const closeErrorSB = () => {
    setErrorSB(false);
    setShowResendButton(false);
  };
  const openSuccessSB = () => setSuccessSB(true);
  const closeSuccessSB = () => setSuccessSB(false);

  const handleSignIn = async () => {
    const result = await dispatch(login({ email, password }));

    // Check for errors
    if (result.type === "get/login/rejected") {
      const errorMsg = result.error?.message || "Failed to sign in. Please check your credentials.";
      setErrorMessage(errorMsg);

      // Check if it's an email verification error
      if (errorMsg.includes("verify your email") || errorMsg.includes("email verification")) {
        setShowResendButton(true);
      } else {
        setShowResendButton(false);
      }

      openErrorSB();
    }
  };

  const handleResendVerification = async () => {
    if (!email || !password) {
      setErrorMessage("Please enter your email and password to resend verification.");
      setShowResendButton(false);
      openErrorSB();
      return;
    }

    setIsResending(true);
    try {
      await resendVerificationEmail(email, password);
      setSuccessMessage("Verification email sent! Please check your inbox.");
      openSuccessSB();
      closeErrorSB();
    } catch (error) {
      setErrorMessage(error.message || "Failed to resend verification email.");
      setShowResendButton(false);
      openErrorSB();
    } finally {
      setIsResending(false);
    }
  };

  const renderErrorSB = (
    <MDSnackbar
      color="error"
      icon="warning"
      title="Error"
      content={errorMessage}
      open={errorSB}
      onClose={closeErrorSB}
      close={closeErrorSB}
      bgWhite
    />
  );

  const renderSuccessSB = (
    <MDSnackbar
      color="success"
      icon="check"
      title="Success"
      content={successMessage}
      open={successSB}
      onClose={closeSuccessSB}
      close={closeSuccessSB}
      bgWhite
    />
  );

  return isAuth ? (
    <Navigate replace to="/tables" />
  ) : (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Sign in
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form">
            <MDBox mb={2}>
              <MDInput
                type="email"
                label="Email"
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="password"
                label="Password"
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
              />
            </MDBox>
            <MDBox mt={4} mb={1}>
              <MDButton variant="gradient" color="info" onClick={handleSignIn} fullWidth>
                sign in
              </MDButton>
            </MDBox>
            {showResendButton && (
              <MDBox mt={2} mb={1}>
                <MDButton
                  variant="outlined"
                  color="warning"
                  onClick={handleResendVerification}
                  disabled={isResending}
                  fullWidth
                >
                  {isResending ? "Sending..." : "Resend Verification Email"}
                </MDButton>
              </MDBox>
            )}
            <MDBox mt={2} mb={1} textAlign="center">
              <MDTypography
                component={Link}
                to="/authentication/reset-password"
                variant="button"
                color="info"
                fontWeight="medium"
                textGradient
              >
                Forgot password?
              </MDTypography>
            </MDBox>
            <MDBox mt={3} mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                Don&apos;t have an account?{" "}
                <MDTypography
                  component={Link}
                  to="/authentication/sign-up"
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                >
                  Sign up
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
      {renderErrorSB}
      {renderSuccessSB}
    </BasicLayout>
  );
}

export default Basic;
